/**
 * Greedy hand-shape decomposition for the Coach. Split out of `Coach.ts` to
 * keep both files under the 200-line cap (mirrors the `GameState`/`stateCore`
 * precedent); re-exported from `Coach.ts`.
 *
 * Strategy: bucket the hand by kind, pull out every triplet, then every run,
 * then a single pair, and call whatever is left a floater. This is a fast
 * best-effort grouping for TEACHING/DISPLAY — it can miss the optimal split a
 * real win needs. `WinChecker.isWinningHand` stays the source of truth for wins.
 */

import {
  KIND_COUNT,
  PAIR_SIZE,
  RANKS,
  SET_SIZE,
  SUIT_KIND_COUNT,
  kindIndex,
  sortTiles,
  type Meld,
  type Tile,
} from '../game/types.js';
import type { CompleteSet, HandShape } from './types.js';

/** A run starting at kind `i` needs i+1/i+2 in the same suit: rank ≤ 7. */
const MAX_RUN_START_OFFSET = RANKS.length - SET_SIZE;

function canStartRun(i: number): boolean {
  return i < SUIT_KIND_COUNT && i % RANKS.length <= MAX_RUN_START_OFFSET;
}

/** Bucket tiles by their dense kind index; each bucket keeps sorted identities. */
function bucketByKind(hand: readonly Tile[]): Tile[][] {
  const byKind: Tile[][] = Array.from({ length: KIND_COUNT }, () => []);
  for (const tile of sortTiles(hand)) {
    const bucket = byKind[kindIndex(tile)];
    if (bucket !== undefined) bucket.push(tile);
  }
  return byKind;
}

function pullRun(byKind: Tile[][], i: number): CompleteSet | null {
  const a = byKind[i];
  const b = byKind[i + 1];
  const c = byKind[i + 2];
  if (a === undefined || b === undefined || c === undefined) return null;
  if (a.length === 0 || b.length === 0 || c.length === 0) return null;
  const t0 = a.shift();
  const t1 = b.shift();
  const t2 = c.shift();
  if (t0 === undefined || t1 === undefined || t2 === undefined) return null;
  return { type: 'run', tiles: [t0, t1, t2] };
}

/** Greedy grouping — triplets first, then runs, then one pair, rest floaters. */
export function analyzeHand(hand: readonly Tile[], melds: readonly Meld[]): HandShape {
  const byKind = bucketByKind(hand);
  const completeSets: CompleteSet[] = [];

  for (let i = 0; i < KIND_COUNT; i += 1) {
    let bucket = byKind[i];
    while (bucket !== undefined && bucket.length >= SET_SIZE) {
      completeSets.push({ type: 'triplet', tiles: bucket.splice(0, SET_SIZE) });
      bucket = byKind[i];
    }
  }

  for (let i = 0; i < SUIT_KIND_COUNT; i += 1) {
    if (!canStartRun(i)) continue;
    let run = pullRun(byKind, i);
    while (run !== null) {
      completeSets.push(run);
      run = pullRun(byKind, i);
    }
  }

  let pair: Tile[] | null = null;
  for (let i = 0; i < KIND_COUNT; i += 1) {
    const bucket = byKind[i];
    if (bucket !== undefined && bucket.length >= PAIR_SIZE) {
      pair = bucket.splice(0, PAIR_SIZE);
      break;
    }
  }

  return { meldCount: melds.length, completeSets, pair, floaters: byKind.flat() };
}
